import { useCallback } from "react";

import { CartService } from "@/components/Cart/CartService";
import { useIsLocalisedPricing } from "@/hooks/useIsLocalisedPricing";
import { TBillingInterval } from "@/types/app";
import { useMutation } from "@tanstack/react-query";
import { CHECKOUT_EVENTS } from "../events.checkout";
import { getCheckoutSummary } from "../handlers/getCheckoutSummary";
import { CheckoutService } from "../service.checkout";
import { CheckoutProduct, TCheckoutType } from "../types.checkout";
import { summaryErrorMessage } from "./useCartSummary";

interface UseCheckoutPromoOptions {
  checkoutType: TCheckoutType;
  billingPeriod: TBillingInterval;
  cartItems: { id: string; quantity?: number }[];
  appliedCodes: string[];
  onApply: (code: string) => void;
  onRemove: (code: string) => void;
}

/**
 * Promo code apply/remove. On apply, calls the summary API and:
 * 1. Stores the full response (including invalid codes) in CartService
 *    so usePromoCodeComponent can detect isInvalidState.
 * 2. Only adds valid codes to CartService's appliedCodes list.
 */
export function useCheckoutPromo({
  checkoutType,
  billingPeriod,
  cartItems,
  appliedCodes,
  onApply,
  onRemove,
}: UseCheckoutPromoOptions) {
  const userToken = CheckoutService.userToken;
  const { isLocalisedPricing } = useIsLocalisedPricing();

  const promoMutation = useMutation({
    mutationFn: async (code: string) => {
      if (!userToken) throw new Error("Not authenticated");

      const products: CheckoutProduct[] = cartItems.map((item) => ({
        product_id: item.id,
        action: "add",
        quantity: item.quantity || 1,
      }));

      const newCodes = [...appliedCodes, code.toUpperCase()];
      return getCheckoutSummary({
        token: userToken,
        payload: { products, interval: billingPeriod, discounts: newCodes },
        checkoutType,
        isLocalisedPricing,
      });
    },
  });

  const handleApplyPromoCode = useCallback(
    async (code: string) => {
      try {
        CartService.setSkipNextSummaryFetch();
        CartService.setSummaryLoading();

        const response = await promoMutation.mutateAsync(code);

        // Always store the full response so invalid codes are visible in the UI
        if (response) {
          CartService.setSummaryData(response);
        }

        // Only apply valid codes to CartService
        const confirmedCodes =
          response?.discounts
            ?.filter((d) => !d.invalid)
            .map((d) => d.coupon_code) ?? [];
        confirmedCodes.forEach(onApply);

        CheckoutService.track(CHECKOUT_EVENTS.PROMO_CODE_APPLIED, {
          types: ["posthog", "customerio"],
          properties: {
            promo_code: code,
            checkout_type: checkoutType,
            billing_period: billingPeriod,
            cart_item_count: cartItems.length,
          },
        });
      } catch (error) {
        CartService.setSummaryError(summaryErrorMessage(error));
        CheckoutService.logger.error("Failed to apply promo code:", error);
      }
    },
    [promoMutation, onApply, checkoutType, billingPeriod, cartItems.length],
  );

  const handleRemoveDiscount = useCallback(
    (code: string) => {
      onRemove(code);

      CheckoutService.track(CHECKOUT_EVENTS.PROMO_CODE_REMOVED, {
        types: ["posthog", "customerio"],
        properties: {
          promo_code: code,
          checkout_type: checkoutType,
          billing_period: billingPeriod,
        },
      });
    },
    [onRemove, checkoutType, billingPeriod],
  );

  return {
    handleApplyPromoCode,
    handleRemoveDiscount,
    isApplying: promoMutation.isPending,
  };
}
